import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { SafeParseError, z } from "zod";

/* Return from Maptiler's API isn't real GeoJSON as it has properties outside
   the "properties" object, sigh. */
const MaptilerFeatureSchema = z.object({
  type: z.literal("Feature"),
  properties: z.object({
    ref: z.string(),
    country_code: z.string().optional(),
    kind: z.string().optional(),
    place_type_name: z.array(z.string().nullable()).optional(),
  }),
  geometry: z.object({
    type: z.literal("Point"),
    coordinates: z.tuple([z.number(), z.number()]),
  }),
  bbox: z.tuple([z.number(), z.number(), z.number(), z.number()]),
  center: z.tuple([z.number(), z.number()]),
  place_name: z.string(),
  place_type: z.array(z.string()),
  relevance: z.number(),
  id: z.string(),
  text: z.string(),
  place_type_name: z.array(z.string().nullable()).optional(),
  context: z
    .array(
      z.object({
        ref: z.string(),
        id: z.string(),
        text: z.string(),
        country_code: z.string().optional(),
        wikidata: z.string().optional(),
        kind: z.string().optional(),
        "osm:place_type": z.string().optional(),
        text_nl: z.string().optional(),
        language: z.string().optional(),
        language_nl: z.string().optional(),
        categories: z.array(z.string()).optional(),
        "osm:tags": z.record(z.string()).optional(),
      })
    )
    .optional(),
  address: z.string().optional(),
  text_nl: z.string().optional(),
  place_name_nl: z.string().optional(),
});

const responseSchema = z.object({
  features: z.array(MaptilerFeatureSchema),
});

type MaptilerGeocodingResponse = z.infer<typeof responseSchema>;
export type MaptilerFeature = MaptilerGeocodingResponse["features"][0];

interface MapTilerGeosearchParams {
  maptilerApiKey: string;
  searchString: string;
  locale?: string;
  bbox?: [number, number, number, number] /* [w, s, e, n] in WGS84 */;
  enabled?: boolean /* Same as react-query, default true */;
}

export function useMaptilerGeosearch({
  maptilerApiKey,
  searchString,
  locale,
  bbox,
  enabled = true,
}: MapTilerGeosearchParams) {
  const isEnabled = !!searchString && !!maptilerApiKey && enabled;
  return useQuery<MaptilerGeocodingResponse, Error>({
    queryKey: ["maptiler", "geocoding", bbox, locale, searchString],
    queryFn: async () => {
      const response = await fetch(
        `https://api.maptiler.com/geocoding/${searchString}.json?key=${maptilerApiKey}${
          bbox ? "&bbox=" + bbox.toString() : ""
        }&language=${locale || "en"}`
      );
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const jsonResponse = await response.json();
      const result = responseSchema.safeParse(jsonResponse);

      if (!result.success) {
        const error = (result as SafeParseError<typeof result>).error;
        console.error(error.format());
        throw new Error("Above error occurred in useMaptilerGeosearch");
      } else {
        return result.data;
      }
    },
    placeholderData: isEnabled ? keepPreviousData : undefined,
    enabled: isEnabled,
  });
}

interface MapTilerReverseGeosearchParams {
  maptilerApiKey: string | undefined;
  coordinate: [number, number] | undefined; // [longitude, latitude]
  locale?: string;
  enabled?: boolean;
  bbox?: [number, number, number, number] /* [w, s, e, n] in WGS84 */;
}

export function useMaptilerReverseGeosearch({
  maptilerApiKey,
  coordinate,
  locale,
  enabled = true,
  bbox,
}: MapTilerReverseGeosearchParams) {
  return useQuery<MaptilerGeocodingResponse, Error>({
    queryKey: ["maptiler", "reverse", coordinate, locale, bbox],
    queryFn: async () => {
      if (!coordinate) throw Error("Never happens");
      const [lon, lat] = coordinate;
      const response = await fetch(
        `https://api.maptiler.com/geocoding/${lon},${lat}.json?key=${maptilerApiKey}${
          bbox ? "&bbox=" + bbox.toString() : ""
        }&language=${locale || "en"}`
      );
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const jsonResponse = await response.json();

      const result = responseSchema.safeParse(jsonResponse);

      if (!result.success) {
        const error = (result as SafeParseError<typeof result>).error;
        console.error(error.format());
        throw new Error("Above error occurred in useMaptilerReverseGeosearch");
      } else {
        return result.data;
      }
    },
    enabled: !!coordinate && !!maptilerApiKey && enabled,
  });
}
